import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Brain, Save, Plus, Trash, Upload, FileText, FolderSearch, Loader2, X, Download } from "lucide-react";
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemoryPanel = ({ 
  open, 
  onOpenChange, 
  userMemory,
  activeConversation,
  onExtractMemory,
  onAddFact,
  onAddInterest,
  onSaveBirthday,
  onClearMemory,
  newFact,
  setNewFact,
  newInterest,
  setNewInterest,
  birthdayDate,
  setBirthdayDate,
  // RAG props
  ragDocuments = [],
  onUploadDocument,
  onDeleteDocument,
  onRefreshDocuments,
  isUploadingDocument = false
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("memory"); // memory | documents
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['.txt', '.pdf', '.md', '.json', '.csv', '.py', '.js', '.html', '.css'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      alert(`${t('error_occurred')}: ${allowedTypes.join(', ')}`);
      return;
    }
    
    // Check file size (max 500KB)
    if (file.size > 500 * 1024) {
      alert(t('error_occurred'));
      return;
    }
    
    await onUploadDocument(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = async (doc) => {
    setDownloadingDoc(doc.document_id);
    try {
      const response = await axios.get(`${API}/tools/rag/documents/${doc.document_id}/download`);
      if (response.data.success) {
        // Create blob and download
        const blob = new Blob([response.data.content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error(t('error_occurred') + ": " + (error.response?.data?.detail || error.message));
    } finally {
      setDownloadingDoc(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Brain size={20} /> {t('ai_memory')}
          </DialogTitle>
        </DialogHeader>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 pb-3">
          <button
            onClick={() => setActiveTab("memory")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "memory" 
                ? "bg-blue-600 text-white" 
                : "bg-zinc-800 text-zinc-400 hover:text-white"
            }`}
          >
            <Brain size={16} className="inline mr-2" />
            {t('memory')}
          </button>
          <button
            onClick={() => { setActiveTab("documents"); onRefreshDocuments && onRefreshDocuments(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "documents" 
                ? "bg-purple-600 text-white" 
                : "bg-zinc-800 text-zinc-400 hover:text-white"
            }`}
          >
            <FolderSearch size={16} className="inline mr-2" />
            {t('mode_file')} (RAG)
          </button>
        </div>
        
        {activeTab === "memory" ? (
          <div className="space-y-6">
            {/* Extract from conversation */}
            {activeConversation && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300 mb-2">{t('extract_from_chat_desc')}</p>
                <Button onClick={onExtractMemory} className="bg-blue-600 hover:bg-blue-700" data-testid="extract-memory-btn">
                  <Save size={16} className="mr-2" /> {t('extract_save')}
                </Button>
              </div>
            )}
            
            {/* Facts */}
            <div>
              <p className="text-zinc-400 text-sm mb-2">📌 {t('facts_label')}</p>
              <div className="flex gap-2 mb-2">
                <Input 
                  placeholder={t('add_fact')} 
                  value={newFact}
                  onChange={(e) => setNewFact(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  onKeyPress={(e) => e.key === "Enter" && onAddFact()}
                />
                <Button onClick={onAddFact} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {userMemory?.facts?.map((fact, i) => (
                  <span key={i} className="bg-zinc-800 px-3 py-1 rounded-full text-sm text-zinc-300">
                    {fact}
                  </span>
                ))}
                {(!userMemory?.facts || userMemory.facts.length === 0) && (
                  <span className="text-zinc-500 text-sm">{t('no_facts_yet')}</span>
                )}
              </div>
            </div>
            
            {/* Interests */}
            <div>
              <p className="text-zinc-400 text-sm mb-2">💜 {t('interests_label')}</p>
              <div className="flex gap-2 mb-2">
                <Input 
                  placeholder={t('add_interest')} 
                  value={newInterest}
                  onChange={(e) => setNewInterest(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  onKeyPress={(e) => e.key === "Enter" && onAddInterest()}
                />
                <Button onClick={onAddInterest} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {userMemory?.interests?.map((interest, i) => (
                  <span key={i} className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm">
                    {interest}
                  </span>
                ))}
                {(!userMemory?.interests || userMemory.interests.length === 0) && (
                  <span className="text-zinc-500 text-sm">{t('no_interests_yet')}</span>
                )}
              </div>
            </div>
            
            {/* Birthday */}
            <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
              <p className="text-pink-300 text-sm mb-2">🎂 {t('birthday')}</p>
              {userMemory?.important_dates?.birthday ? (
                <p className="text-white">{userMemory.important_dates.birthday}</p>
              ) : (
                <div className="flex gap-2">
                  <Input 
                    type="date"
                    value={birthdayDate}
                    onChange={(e) => setBirthdayDate(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="birthday-input"
                  />
                  <Button onClick={onSaveBirthday} className="bg-pink-600 hover:bg-pink-700" data-testid="save-birthday-btn">
                    {t('save')}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Last Topics */}
            {userMemory?.last_topics?.length > 0 && (
              <div>
                <p className="text-zinc-400 text-sm mb-2">💬 {t('last_topics')}</p>
                <div className="flex flex-wrap gap-2">
                  {userMemory.last_topics.map((topic, i) => (
                    <span key={i} className="bg-zinc-700 px-3 py-1 rounded-full text-sm text-zinc-300">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Clear Memory */}
            <div className="pt-4 border-t border-zinc-800">
              <Button onClick={onClearMemory} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                <Trash size={16} className="mr-2" /> {t('clear_memory')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* RAG Info */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <p className="text-purple-300 text-sm mb-2">📚 {t('rag_title')}</p>
              <p className="text-zinc-400 text-xs">
                {t('rag_desc')}
              </p>
            </div>
            
            {/* Upload Section */}
            <div>
              <p className="text-zinc-400 text-sm mb-2">{t('upload_document')}</p>
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".txt,.pdf,.md,.json,.csv,.py,.js,.html,.css"
                className="hidden"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingDocument}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="upload-document-btn"
              >
                {isUploadingDocument ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> {t('loading')}</>
                ) : (
                  <><Upload size={16} className="mr-2" /> {t('select_file')}</>
                )}
              </Button>
              <p className="text-zinc-500 text-xs mt-2">
                .txt, .pdf, .md, .json, .csv, .py, .js, .html, .css (max 500KB)
              </p>
            </div>
            
            {/* Documents List */}
            <div>
              <p className="text-zinc-400 text-sm mb-2">{t('documents')} ({ragDocuments.length})</p>
              {ragDocuments.length === 0 ? (
                <div className="bg-zinc-800 rounded-lg p-6 text-center">
                  <FileText size={32} className="mx-auto text-zinc-600 mb-2" />
                  <p className="text-zinc-500 text-sm">{t('no_documents_yet')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {ragDocuments.map((doc) => (
                    <div 
                      key={doc.document_id}
                      className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-purple-400" />
                        <div>
                          <p className="text-white text-sm">{doc.filename}</p>
                          <p className="text-zinc-500 text-xs">
                            {doc.total_chunks} • {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          disabled={downloadingDoc === doc.document_id}
                          className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          data-testid={`download-doc-${doc.document_id}`}
                        >
                          {downloadingDoc === doc.document_id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteDocument(doc.document_id)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
